import type { FilterState, PetType } from './filterTypes';
import { UNION_FILTER_KEYS } from './filterStateToV3';
import { VALID_PROPERTY_TYPE_VALUES, VALID_RENTAL_TYPE_VALUES } from './filterTypes';
import type { BedsFilterValue } from './bedroomMappings';
import type { BathsFilterValue } from './bathroomMappings';

export type FilterPill = {
  key: keyof FilterState;
  value?: string;
  label: string;
};

const DISPLAY_LABELS: Record<string, string> = {
  cats: 'Cats',
  dogs: 'Dogs',
  inUnit: 'In-unit laundry',
  shared: 'Shared laundry',
  cooling: 'A/C',
  heating: 'Heating',
  parking: 'Parking',
  gatedEntry: 'Gated entry',
  doorman: 'Doorman',
  fitnessCenter: 'Gym',
  swimmingPool: 'Pool',
  dishwasher: 'Dishwasher',
  apartment: 'Apartment',
  condo: 'Condo',
  duplex: 'Duplex',
  house: 'House',
  townhouse: 'Townhouse',
  rental: 'Regular',
  room: 'Rooms for rent',
  sublet: 'Sublet',
  corporate: 'Corporate',
  requiresPhotos: 'Has photos',
  requiresPrice: 'Has price',
  forRentByOwner: 'For rent by owner',
  hasOffers: 'Has offers',
  acceptingApplications: 'Accepting applications',
};

const RESTRICTION_LABELS: Record<string, string> = {
  income: 'Income restricted',
  senior: 'Senior housing',
  student: 'Student housing',
  military: 'Military housing',
  none: 'No restrictions',
};

const CREATED_WITHIN_LABELS: Record<string, string> = {
  hour: 'Last hour',
  day: 'Last 24 hours',
  week: 'Last 7 days',
  month: 'Last 30 days',
};

const ORDER_BY_LABELS: Record<string, string> = {
  activated: 'Newest',
  weekViews: 'Most Popular',
  highPrice: 'High Price',
  lowPrice: 'Low Price',
};

function formatCurrency(value: number): string {
  return `$${value.toLocaleString('en-US')}`;
}

function priceLabel(price: FilterState['price']): string | null {
  const { min, max } = price;
  if (min === null && max === null) return null;
  if (min !== null && max === null) return `${formatCurrency(min)}+`;
  if (min === null && max !== null) return `Up to ${formatCurrency(max)}`;
  return `${formatCurrency(min!)}\u2013${formatCurrency(max!)}`;
}

function bedsLabel(bedrooms: FilterState['bedrooms']): string | null {
  if (bedrooms.min === null) return null;
  if (bedrooms.min === 0 && bedrooms.max === 0) return 'Studio';
  if (bedrooms.min !== null && bedrooms.min === bedrooms.max) {
    return `${bedrooms.min} bed${bedrooms.min === 1 ? '' : 's'}`;
  }
  if (bedrooms.max !== null) return `${bedrooms.min}–${bedrooms.max} beds`;
  return `${bedrooms.min}+ beds`;
}

function bathsLabel(bathrooms: FilterState['bathrooms']): string | null {
  if (bathrooms.min === null) return null;
  return `${bathrooms.min}+ baths`;
}

function sqftLabel(sqft: FilterState['sqft']): string | null {
  const { min, max } = sqft;
  if (min === null && max === null) return null;
  if (min !== null && max === null) return `${min.toLocaleString('en-US')}+ sqft`;
  if (min === null && max !== null) return `Up to ${max.toLocaleString('en-US')} sqft`;
  return `${min!.toLocaleString('en-US')}\u2013${max!.toLocaleString('en-US')} sqft`;
}

function moveInDateLabel(moveInDate: FilterState['moveInDate']): string | null {
  const moveInBy = moveInDate.end ?? moveInDate.start;
  return moveInBy ? `By ${moveInBy}` : null;
}

function multiSelectPills(
  key: keyof FilterState,
  values: Array<string>,
): Array<FilterPill> {
  return values.map((value) => ({
    key,
    value,
    label: DISPLAY_LABELS[value] ?? value,
  }));
}

// Property/listing types are all-or-nothing sets: every member selected means the
// generic "show all" slug (no chip), while a strict subset shows one chip per member.
export function hasEveryMember(selected: ReadonlyArray<string>, all: ReadonlySet<string>): boolean {
  if (selected.length < all.size) return false;
  const set = new Set(selected);
  for (const member of all) {
    if (!set.has(member)) return false;
  }
  return true;
}

export function displayedTypes<T extends string>(selected: ReadonlyArray<T>, all: ReadonlySet<string>): Array<T> {
  return hasEveryMember(selected, all) ? [] : [...selected];
}

export function getActiveFilterPills(filters: FilterState): Array<FilterPill> {
  const pills: Array<FilterPill> = [];

  // Price (single pill for range)
  const pLabel = priceLabel(filters.price);
  if (pLabel) pills.push({ key: 'price', label: pLabel });

  // Beds (single pill)
  const bLabel = bedsLabel(filters.bedrooms);
  if (bLabel) pills.push({ key: 'bedrooms', label: bLabel });

  // Baths (single pill)
  const baLabel = bathsLabel(filters.bathrooms);
  if (baLabel) pills.push({ key: 'bathrooms', label: baLabel });

  // Pets (per-value pills)
  pills.push(...multiSelectPills('pets', filters.pets));

  // Laundry (per-value pills)
  pills.push(...multiSelectPills('laundry', filters.laundry));

  // Amenities (per-value pills)
  pills.push(...multiSelectPills('amenities', filters.amenities));

  // Furnished (single pill)
  if (filters.furnished) pills.push({ key: 'furnished', label: 'Furnished' });

  // Property Types (per-value pills; all selected = "show all" → no pills)
  pills.push(...multiSelectPills('propertyTypes', displayedTypes(filters.propertyTypes, VALID_PROPERTY_TYPE_VALUES)));

  // Sqft (single pill)
  const sLabel = sqftLabel(filters.sqft);
  if (sLabel) pills.push({ key: 'sqft', label: sLabel });

  // Created Within (single pill)
  if (filters.createdWithin) {
    pills.push({
      key: 'createdWithin',
      label: CREATED_WITHIN_LABELS[filters.createdWithin] ?? filters.createdWithin,
    });
  }

  // Rental Types (per-value pills; all selected = "show all" → no pills)
  pills.push(...multiSelectPills('rentalTypes', displayedTypes(filters.rentalTypes, VALID_RENTAL_TYPE_VALUES)));

  // Restrictions (single pill)
  if (filters.restrictions !== null) {
    pills.push({
      key: 'restrictions',
      label: RESTRICTION_LABELS[filters.restrictions] ?? filters.restrictions,
    });
  }

  // Move-in Date (single pill)
  const mLabel = moveInDateLabel(filters.moveInDate);
  if (mLabel) pills.push({ key: 'moveInDate', label: mLabel });

  // Keywords (single pill)
  if (filters.keywords.trim()) {
    pills.push({ key: 'keywords', label: `"${filters.keywords.trim()}"` });
  }

  // Additional Opts (per-value pills)
  pills.push(...multiSelectPills('additionalOpts', filters.additionalOpts));

  // Commute (single pill)
  if (filters.commute) {
    pills.push({ key: 'commute', label: `Commute: ${filters.commute.minutes} min` });
  }

  // OrderBy (single pill — shown in pills but not counted as a "filter")
  if (filters.orderBy) {
    pills.push({
      key: 'orderBy',
      label: ORDER_BY_LABELS[filters.orderBy] ?? filters.orderBy,
    });
  }

  return pills;
}

const INLINE_STRIP_FILTER_KEYS = new Set<keyof FilterState>(['price', 'bedrooms', 'bathrooms']);
const SORT_FILTER_KEY: keyof FilterState = 'orderBy';

/**
 * Drops the pills that are not filters — a sort is rendered as a pill but does
 * not narrow the result set, so removing it can never change how many listings
 * match.
 */
export function getRemovableFilterPills(pills: Array<FilterPill>): Array<FilterPill> {
  return pills.filter(({ key }) => key !== SORT_FILTER_KEY);
}

/**
 * The pills to offer as relief on a zero-result search: removable pills, with
 * each {@link UNION_FILTER_KEYS} category collapsed into a single chip that
 * clears the whole category.
 *
 * Without the collapse, "House" and "Condo" would be offered separately and
 * clicking either would leave a stricter search than before — relief that makes
 * things worse.
 */
export function getReliefFilterPills(pills: Array<FilterPill>): Array<FilterPill> {
  const removable = getRemovableFilterPills(pills);
  const collapsed = new Set<keyof FilterState>();

  return removable.flatMap((pill) => {
    if (!UNION_FILTER_KEYS.has(pill.key)) return [pill];
    if (collapsed.has(pill.key)) return [];
    collapsed.add(pill.key);
    const label = removable
      .filter((other) => other.key === pill.key)
      .map((other) => other.label)
      .join(', ');
    return [{ key: pill.key, label }];
  });
}

function countActiveFilterPills(filters: FilterState, excludedKeys?: ReadonlySet<keyof FilterState>): number {
  return getRemovableFilterPills(getActiveFilterPills(filters)).filter(({ key }) => !excludedKeys?.has(key)).length;
}

/**
 * Counts active filter selections.
 * orderBy is excluded from the count (it's a sort, not a filter).
 */
export function getActiveFilterCount(filters: FilterState): number {
  return countActiveFilterPills(filters);
}

// --- Popover button labels (always return a string, even for default state) ---

export function getPriceButtonLabel(price: FilterState['price']): string {
  return priceLabel(price) ?? 'Any price';
}

export function getBedsButtonLabel(value: BedsFilterValue): string {
  if (value.selected === 'any') return 'All beds';
  const num = Number(value.selected);
  if (value.max !== null && num === value.max) return `${num} bed${num === 1 ? '' : 's'}`;
  if (value.max !== null) return `${num}–${value.max} beds`;
  return `${value.selected}+ beds`;
}

export function getBathsButtonLabel(value: BathsFilterValue): string {
  if (value === 'any') return 'All baths';
  return `${value}+ baths`;
}

export function getPetsButtonLabel(pets: Array<PetType>): string {
  if (pets.length === 0) return 'Pets';
  if (pets.length === 2) return 'All pets';
  return pets.map((v) => DISPLAY_LABELS[v] ?? v).join(', ');
}

/**
 * Counts active Rooms filters (bedrooms + bathrooms). Added to the AllFilters
 * badge when the Rooms trigger is collapsed into the modal.
 */
export function getRoomsFiltersCount(filters: FilterState): number {
  let count = 0;
  if (filters.bedrooms.min !== null) count++;
  if (filters.bathrooms.min !== null) count++;
  return count;
}

/**
 * Counts active Price filters. Added to the AllFilters badge when the Price
 * trigger is collapsed into the modal.
 */
export function getPriceFiltersCount(filters: FilterState): number {
  return filters.price.min !== null || filters.price.max !== null ? 1 : 0;
}

/**
 * Counts active filters excluding the inline-strip trio (price, bedrooms, bathrooms),
 * which carry their own active-state styling on their triggers. Powers the
 * AllFilters button badge.
 */
export function getMoreFiltersCount(filters: FilterState): number {
  return countActiveFilterPills(filters, INLINE_STRIP_FILTER_KEYS);
}
